import { useState, useRef, useEffect } from 'react';

export type ViewMode = 'day' | 'week5' | 'week7' | 'month' | 'year' | 'schedule' | 'compare' | 'reconcile' | 'maintenance';

interface ViewOption {
  value: ViewMode;
  label: string;
  shortcut: string;
}

const VIEW_OPTIONS: ViewOption[] = [
  { value: 'day', label: 'Day', shortcut: 'D' },
  { value: 'week5', label: 'Week', shortcut: 'W' },
  { value: 'week7', label: 'Week (7-day)', shortcut: 'W' },
  { value: 'month', label: 'Month', shortcut: 'M' },
  { value: 'year', label: 'Year', shortcut: 'Y' },
  { value: 'schedule', label: 'Schedule', shortcut: 'A' },
  { value: 'compare', label: 'Compare', shortcut: 'C' },
  { value: 'reconcile', label: 'Reconcile', shortcut: 'R' },
  { value: 'maintenance', label: 'Maintenance', shortcut: 'X' },
];

interface ViewSelectorProps {
  viewMode: ViewMode;
  onChange: (mode: ViewMode) => void;
  hiddenOptions?: ViewMode[];
}

export default function ViewSelector({ viewMode, onChange, hiddenOptions }: ViewSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentOption = VIEW_OPTIONS.find(o => o.value === viewMode) || VIEW_OPTIONS[3]; // default to month

  const getDisplayLabel = () => {
    if (viewMode === 'week5') return 'Week';
    if (viewMode === 'week7') return 'Week (7)';
    return currentOption.label;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-lg border border-slate-600 transition-colors"
      >
        <span>{getDisplayLabel()}</span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-44 bg-slate-800 rounded-lg border border-slate-600 shadow-xl z-50 py-1">
          {VIEW_OPTIONS.filter(o => !hiddenOptions?.includes(o.value)).map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-slate-700 transition-colors ${
                viewMode === option.value ? 'text-blue-400' : 'text-slate-300'
              }`}
            >
              <span>{option.label}</span>
              <span className="text-xs text-slate-500">{option.shortcut}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
